/**
 * Capa MULTIPAÍS del portal del asesor (docs/179).
 *
 * Qué países existen y si se venden lo dice la API (`/v1/public/cd2b/paises`): aquí NO hay lista de
 * respaldo. Lo de este archivo son solo CONVENCIONES DE PRESENTACIÓN de cada país — cómo se llama su
 * documento fiscal, su prefijo telefónico, su moneda de referencia — para que todas las pantallas
 * digan lo mismo. Un país que la API habilite y no esté aquí se muestra con valores neutros (USD,
 * sin moneda de referencia), nunca con los de El Salvador.
 *
 * Dinero: el precio de lista y las comisiones son en USD en todos los países. En GT y HN se muestra
 * además el equivalente APROXIMADO en su moneda, con la misma tasa que publica cd-2b.com
 * (portal-nextjs/src/content/cd2b/paises-contenido.ts).
 */

export const CONVENCIONES = {
  SV: {
    nombre: 'El Salvador',
    locale: 'es-SV',
    prefijo: '+503',
    docFiscal: { principal: 'NIT', registro: 'NRC' },
    division: ['Departamento', 'Distrito'],
    referencia: null,
    bandera: ['#0047AB', '#FFFFFF', '#0047AB'],
  },
  GT: {
    nombre: 'Guatemala',
    locale: 'es-GT',
    prefijo: '+502',
    docFiscal: { principal: 'NIT', registro: null },
    division: ['Departamento', 'Municipio'],
    referencia: { codigo: 'GTQ', simbolo: 'Q', tasa: 7.63, fuente: 'Banguat, 23-sep-2026' },
    bandera: ['#4997D0', '#FFFFFF', '#4997D0'],
  },
  HN: {
    nombre: 'Honduras',
    locale: 'es-HN',
    prefijo: '+504',
    docFiscal: { principal: 'RTN', registro: null },
    division: ['Departamento', 'Municipio'],
    referencia: { codigo: 'HNL', simbolo: 'L', tasa: 27, fuente: 'BCH, 25-sep-2026' },
    bandera: ['#0073CF', '#FFFFFF', '#0073CF'],
  },
}

const NEUTRA = { nombre: null, locale: 'es', prefijo: '', docFiscal: { principal: 'ID fiscal', registro: null }, division: ['Región', 'Localidad'], referencia: null, bandera: null }

/** Convenciones de un país (neutras si el portal no lo conoce todavía). */
export function convencion(pais) {
  const c = CONVENCIONES[String(pais || '').toUpperCase()]
  return c || { ...NEUTRA, nombre: pais || null }
}

const fmtUsd = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Un monto en USD tal como se escribe en todo el portal: «$ 1,234.50». `centavos: true` cuando el
 * número viene en centavos (comisiones y retiros de la API).
 */
export function usd(monto, { centavos = false } = {}) {
  const n = Number(monto) || 0
  return `$ ${fmtUsd.format(centavos ? n / 100 : n)}`
}

/**
 * El equivalente aproximado en la moneda del país («≈ Q 9,529.87»), o null donde se usa el dólar.
 */
export function referenciaLocal(monto, pais, { centavos = false } = {}) {
  const ref = convencion(pais).referencia
  if (!ref) return null
  const n = (Number(monto) || 0) / (centavos ? 100 : 1)
  const fmt = new Intl.NumberFormat(convencion(pais).locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `≈ ${ref.simbolo} ${fmt.format(n * ref.tasa)}`
}

/** USD + referencia local en una sola cadena: «$ 120.00 (≈ Q 915.60)». */
export function dineroConReferencia(monto, pais, opts) {
  const ref = referenciaLocal(monto, pais, opts)
  return ref ? `${usd(monto, opts)} (${ref})` : usd(monto, opts)
}

/** Fecha corta en el idioma del país: «13 oct 2026». */
export function fechaCorta(fecha, pais) {
  if (!fecha) return '—'
  const d = new Date(fecha)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(convencion(pais).locale, { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Estilo CSS de la bandera (tres franjas) para el chip de país. */
export function estiloBandera(pais) {
  const b = convencion(pais).bandera
  if (!b) return { background: 'hsl(var(--muted))' }
  return { background: `linear-gradient(${b[0]} 0 33%, ${b[1]} 33% 67%, ${b[2]} 67%)` }
}
